import { useMemo, useState } from 'react';
import type { Comment, Item, Person } from '../lib/types';
import { Icon } from '../components/Icon';
import { CommentInput } from './CommentInput';
import { Thread } from './Thread';
import type { CommentActions } from './useComments';

export type CommentFilter = 'open' | 'resolved' | 'all';

interface Props {
  item: Item;
  projectId: string;
  people: Person[];
  comments: Comment[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  filter: CommentFilter;
  onFilterChange: (filter: CommentFilter) => void;
  selectedId: string | null;
  highlightId: string | null;
  canComment: boolean;
  canManage: boolean;
  currentUserId: string | null;
  currentVersionId: string | null;
  actions: CommentActions;
  linkFor: (comment: Comment) => string;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  open: boolean;
  onClose: () => void;
}

/** The running list of feedback for one review item. */
export function CommentSidebar({
  item,
  projectId,
  people,
  comments,
  loading,
  error,
  onRetry,
  filter,
  onFilterChange,
  selectedId,
  highlightId,
  canComment,
  canManage,
  currentUserId,
  currentVersionId,
  actions,
  linkFor,
  onSelect,
  onHover,
  open,
  onClose,
}: Props) {
  const [newestFirst, setNewestFirst] = useState(false);
  const openCount = comments.filter((c) => c.status !== 'resolved').length;
  const resolvedCount = comments.length - openCount;

  const visible = useMemo(() => {
    const list = comments.filter((c) => (filter === 'all' ? true : filter === 'resolved' ? c.status === 'resolved' : c.status !== 'resolved') || c.id === selectedId);
    const sorted = [...list].sort((a, b) => {
      // Video feedback reads best in running order.
      if (item.type === 'video') {
        const ta = a.anchor.type === 'time' ? a.anchor.time : Number.POSITIVE_INFINITY;
        const tb = b.anchor.type === 'time' ? b.anchor.time : Number.POSITIVE_INFINITY;
        if (ta !== tb) return ta - tb;
      }
      return a.number - b.number;
    });
    return newestFirst ? sorted.reverse() : sorted;
  }, [comments, filter, selectedId, item.type, newestFirst]);

  return (
    <aside className={`side ${open ? 'is-open' : ''}`} aria-label="Revisions">
      <div className="side-head">
        <div className="row-between">
          <h2 style={{ fontSize: 'var(--fs-lg)' }}>Revisions</h2>
          <div className="row" style={{ gap: 4 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNewestFirst((value) => !value)} aria-label={newestFirst ? 'Showing newest first' : 'Showing in order'}>
              {newestFirst ? 'Newest first' : item.type === 'video' ? 'By timecode' : 'Oldest first'}
            </button>
            <button type="button" className="icon-btn side-toggle" aria-label="Close revisions" onClick={onClose}>
              <Icon name="x" />
            </button>
          </div>
        </div>
        <div className="segmented" role="group" aria-label="Filter revisions">
          <button type="button" aria-pressed={filter === 'open'} onClick={() => onFilterChange('open')}>
            Open <span className="muted">{openCount}</span>
          </button>
          <button type="button" aria-pressed={filter === 'resolved'} onClick={() => onFilterChange('resolved')}>
            Resolved <span className="muted">{resolvedCount}</span>
          </button>
          <button type="button" aria-pressed={filter === 'all'} onClick={() => onFilterChange('all')}>
            All
          </button>
        </div>
      </div>

      <div className="side-list">
        {loading && comments.length === 0 ? (
          <div className="side-empty">
            <span className="spinner" />
          </div>
        ) : error && comments.length === 0 ? (
          <div className="side-empty" role="alert">
            <Icon name="alert" className="icon-lg" />
            <strong style={{ color: 'var(--text-primary)' }}>Revisions could not be loaded</strong>
            <span>{error}</span>
            {onRetry && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
                <Icon name="refresh" /> Try again
              </button>
            )}
          </div>
        ) : visible.length === 0 ? (
          <div className="side-empty">
            <Icon name="message" className="icon-lg" />
            <strong style={{ color: 'var(--text-primary)' }}>{filter === 'resolved' ? 'Nothing resolved yet' : comments.length ? 'Revisions complete' : 'No revisions yet'}</strong>
            {canComment && filter !== 'resolved' && (
              <span>
                {item.type === 'video'
                  ? 'Press Add revision, pause where something should change, and click the frame.'
                  : 'Press Add revision and click anywhere on the ' + (item.type === 'website' ? 'page' : 'image') + ' to drop a pin.'}
              </span>
            )}
          </div>
        ) : (
          visible.map((comment) => (
            <Thread
              key={comment.id}
              comment={comment}
              item={item}
              projectId={projectId}
              people={people}
              selected={comment.id === selectedId}
              highlighted={comment.id === highlightId}
              dimmed={!!comment.versionId && !!currentVersionId && comment.versionId !== currentVersionId}
              canComment={canComment}
              canManage={canManage}
              currentUserId={currentUserId}
              actions={actions}
              linkFor={linkFor}
              onSelect={() => onSelect(comment.id === selectedId ? null : comment.id)}
              onHover={(hovering) => onHover(hovering ? comment.id : null)}
            />
          ))
        )}
      </div>

      {canComment && (
        <div className="side-foot">
          <CommentInput
            projectId={projectId}
            people={people}
            placeholder={`General note on this ${item.type}…`}
            submitLabel="Post"
            showCategory
            rows={1}
            onSubmit={async (draft) => {
              const created = await actions.create({ ...draft, itemId: item.id });
              onSelect(created.id);
            }}
          />
        </div>
      )}
    </aside>
  );
}
